import { useChain } from '@cosmos-kit/react';
import { WalletStatus as CosmosWalletStatus } from '@cosmos-kit/core';

// Gravity Bridge chain name in cosmos-kit
export const CHAIN_NAME = 'gravitybridge';

export interface WalletState {
  // Connection status
  isConnected: boolean;
  isConnecting: boolean;
  isDisconnected: boolean;
  isRejected: boolean;
  isNotExist: boolean;
  isError: boolean;
  status: CosmosWalletStatus;

  // User info
  address: string | undefined;
  username: string | undefined;
  
  // Wallet info
  walletName: string | undefined;
  walletIcon: string | undefined;
  
  // Actions
  connect: () => Promise<void>;
  disconnect: () => Promise<void>;
  openView: () => void;
  
  // Error info
  message: string | undefined;
}

/**
 * Custom hook that wraps cosmos-kit's useChain for Gravity Bridge
 * Provides a simplified interface for wallet connection across all pages
 */
export function useWallet(): WalletState {
  const {
    connect,
    disconnect,
    openView,
    status,
    username,
    address,
    message,
    wallet,
  } = useChain(CHAIN_NAME);

  return {
    // Connection status
    isConnected: status === CosmosWalletStatus.Connected,
    isConnecting: status === CosmosWalletStatus.Connecting,
    isDisconnected: status === CosmosWalletStatus.Disconnected,
    isRejected: status === CosmosWalletStatus.Rejected,
    isNotExist: status === CosmosWalletStatus.NotExist,
    isError: status === CosmosWalletStatus.Error,
    status,

    // User info
    address,
    username,

    // Wallet info
    walletName: wallet?.prettyName,
    walletIcon: wallet?.logo as string | undefined,

    // Actions
    connect,
    disconnect,
    openView,

    // Error info
    message,
  };
}

/**
 * Helper function to truncate address for display
 */
export function truncateAddress(address: string | undefined, prefixLength = 10, suffixLength = 6): string {
  if (address === undefined || address === '') return '';
  if (address.length <= prefixLength + suffixLength) return address;
  return `${address.slice(0, prefixLength)}...${address.slice(-suffixLength)}`;
}
